import RateUtils from './rate';

export class Job {
    /**
     * A Job is a task that is to be triggered on demand, but only actually run
     * under specific conditions. This allows us to wrap the task with
     * throttling, only-run-once-at-a-time semantics, etc.
     *
     * rateLimitPeriod:
     *   Number of milliseconds required between the start of any two runs. Any
     *   triggers within this threshold will be blocked.
     *   Default: none.
     *
     * rateLimitFollowUp:
     *   If a trigger is blocked due to a rate limit, wait for the rate limit to
     *   finish and then run the task.
     *   Default: false.
     *
     * parallelRuns:
     *   Allow running a task again before the previous run has finished.
     *   Default: false.
     *
     * parallelRunFollowUp:
     *   If a trigger is blocked due to `parallelRuns` being disabled, wait for
     *   the previous run to finish and then run the task again.
     *   Default: false.
     *
     * TODO: should probably simplify these names/options a bit. For one, we
     * could probably just use one `followUp` instead of individual options.
     *
     * @param {Function} func
     * @param {object}  [options]
     * @param {number}  options.[rateLimitPeriod]
     * @param {boolean} options.[rateLimitFollowUp]
     * @param {boolean} options.[parallelRuns]
     * @param {boolean} options.[parallelRunFollowUp]
     */
    constructor(func, options) {
        let rateLimitPeriod = null;
        if (options && options.rateLimitPeriod) {
            rateLimitPeriod = options.rateLimitPeriod;
        }

        let rateLimitFollowUp = false;
        if (options && typeof options.rateLimitFollowUp === 'boolean') {
            rateLimitFollowUp = options.rateLimitFollowUp;
        }

        let parallelRuns = false;
        if (options && typeof options.parallelRuns === 'boolean') {
            parallelRuns = options.parallelRuns;
        }

        let parallelRunFollowUp = false;
        if (options && typeof options.parallelRunFollowUp === 'boolean') {
            parallelRunFollowUp = options.parallelRunFollowUp;
        }

        let throttler = null;
        if (rateLimitPeriod) {
            throttler = new RateUtils.Throttler({
                period: rateLimitPeriod,
            });
        }

        this.func = func;
        this.throttler = throttler;
        this.rateLimitPeriod = rateLimitPeriod;
        this.rateLimitFollowUp = rateLimitFollowUp;
        this.parallelRuns = parallelRuns;
        this._startCount = 0;
        this._endCount = 0;
        this._runPromise = null;
        this._queuePromise = null;
    }

    isRunning() {
        return this._startCount > this._endCount;
    }

    async trigger() {
        const run = async () => {
            const promise = new Promise(async resolve => {
                this._startCount += 1;
                try {
                    if (this.throttler) {
                        this.throttler.recordCall();
                    }
                    await this.func();
                } finally {
                    this._endCount += 1;
                    resolve();
                }
            });

            this._runPromise = promise;
            try {
                await promise;
            } finally {
                if (this._runPromise === promise) {
                    this._runPromise = null;
                }
            }
        };

        // Handle rate limit
        if (this.throttler && !this.throttler.canCall()) {
            if (this.rateLimitFollowUp) {
                await this._queueTrigger();
            }
            return;
        }

        // Handle parallel runs
        if (this.isRunning() && !this.parallelRuns) {
            if (this.parallelRunFollowUp) {
                await this._queueTrigger();
            }
            return;
        }

        // We're not restricted by rate limiting, nor by parallel calls, so just
        // run the task.
        await run();
    }

    async _queueTrigger() {
        // We only have to schedule a re-run once; the rest of the calls can
        // piggyback on that promise.
        if (this._queuePromise) {
            await this._queuePromise;
            return;
        }

        // Schedule a re-run
        const startCount = this._startCount;
        const promise = new Promise(async resolve => {
            while (true) {

                // If a new run has finished since we started trying to schedule
                // a re-run, then we don't have to schedule anything and we can
                // quit.
                if (this._endCount > startCount) {
                    break;
                }

                // If parallel runs aren't allowed, then we have to wait for the
                // current run to finish.
                if (!this._parallelRuns && this._runPromise) {
                    try {
                        await this._runPromise;
                    } catch (err) {}
                    continue;
                }

                // If rate limiting is enabled, then we have to respect that.
                if (this.throttler && !this.throttler.canCall()) {
                    await this.throttler.wait();
                    continue;
                }

                // Re-run
                await this.trigger();
                break;
            }
            resolve();
        });

        // Wait for the re-run
        this._queuePromise = promise;
        try {
            await this._queuePromise;
        } finally {
            this._queuePromise = null;
        }
    }
}

export default {
    Job,
};



// WEBPACK FOOTER //
// ./src/utils/jobs.js